<template>
  <div class="trajectory-container">
    <div class="top-area">
      <i class="icon el-icon-arrow-left" @click="backHandle"></i>
      <div class="replay-right">
        <div class="line-options">
          <el-radio-group v-model="radio" @input="radioChangeHandle">
            <el-radio v-model="radio" label="line">线</el-radio>
            <el-radio v-model="radio" label="heatmapBlock">网格</el-radio>
          </el-radio-group>
        </div>
        <weatherView :weatheInfo="weatherMod" />
      </div>
    </div>
    <div class="map-control">
      <BMap ref="BMapRef" @loaded="onMapLoaded"></BMap>
    </div>
    <div class="sensor-area">
      <sensorView :seneorData="sensorList" @toggleSensor="toggleSensorHandler" />
    </div>
    <ProgressBar
      style="pointer-events: all"
      ref="progressBar"
      v-bind="progressBarConfig"
      :defaultActive="beginTimeStr"
      :speed="speed"
      @change="handleChange"
      @jumpto="handleJumpto"
      @playState="handlePlayState"
    />
  </div>
</template>

<script>
import { boxOriginDataServer } from '@/api/drone';
import { getWirelessBoxListServer } from '@/api/devices';

import { isEmpty } from '@/utils/is';
import { Engine, EmptySky } from 'mapv-three';
import { GeoModel } from '@/utils/bmap';
import { mapState } from 'vuex';
import { toBmapPos2 } from '@/utils/bmapUtil';

import ctt from '@/utils/const';
import BMap from '@/components/bmapGL/index.vue';
import sensorView from './components/history-sensor.vue';
import bmapTheme from '@/utils/bmapTheme';
import ProgressBar from '@/components/progressBar';
import weatherView from './real-data/components/weatherInfoView.vue';
import { getTime, toTimestamp } from '@/utils/date';

function search(nums, [min, max]) {
  let left = 0;
  let right = nums.length - 1;
  let mid = -1;
  while (left <= right) {
    mid = Math.floor((right - left) / 2) + left;
    const num = nums[mid];
    if (num > min && num < max) {
      return mid;
    } else if (num > max) {
      right = mid - 1;
    } else {
      left = mid + 1;
    }
  }
  return right;
}

export default {
  components: {
    BMap,
    ProgressBar,
    sensorView,
    weatherView
  },
  computed: {
    ...mapState('app', ['theme']),
    ...mapState('socket', ['deviceSNMap'])
  },
  watch: {
    theme() {
      this.setMapThemeHandler();
    }
  },
  data() {
    return {
      radio: 'line',
      sensorindex: 0,
      tasks: [],
      data: [],
      sn: this.$route.query.sn,
      max: {
        'pm10.0浓度': 200,
        'pm2.5浓度': 9999999
      },
      sensorDict: {},
      dataCache: {},
      config: {
        // center: [116.572656, 40.088812, 0],
        // center: [116.403963, 39.91512, 0],
        center: [12956455.078979049, 4825523.581885624, 10],
        pitch: 60,
        zoom: 18
      },
      progressBarConfig: {
        frame: 1,
        maskBg: 'linear-gradient(rgb(0,0,0, 0.3), rgba(0,0,0, 0.8))',
        cacheRanges: [],
        range: [],
        status: 0
      },
      sensorList: [],
      speed: 2,
      currentIndex: -1,
      pageConfig: {
        offset: 1,
        allPages: 0
      },
      beginTimeStr: 0,
      weatherMod: {
        humidity: 0, // 湿度
        temp: 0, //温度
        windDirection: '0', // 风向
        windSpeedValue: '0' // 风速
      }
    };
  },
  async mounted() {
    this.currentDeviceType = this.deviceSNMap[this.$route.query.sn].deviceType;
    this.res = null;
    this.beginTimeStr = toTimestamp(this.$route.query.beginTime, 'ms');
    this.endTimeStr = toTimestamp(this.$route.query.endTime, 'ms');

    this.progressBarConfig.range.push(this.beginTimeStr, this.endTimeStr);
    this.progressBarConfig.cacheRanges.push([this.beginTimeStr, this.endTimeStr]);

    this.firstChangePosition = false;
    this.initBoxDeviceHandler();
    await this.initOriginDataHandler();
    this.setMapThemeHandler();
  },
  methods: {
    setMapThemeHandler() {
      if (this.$map) {
        this.$map.setMapStyleV2({ styleJson: bmapTheme[this.theme] });
      }
    },
    matchData(time) {
      let multiple = 0.5 * this.speed;
      multiple = multiple < 1.5 ? 1.5 : multiple;
      let boundary = getTime('s') * multiple;
      const range = [time - boundary, time];
      const times = Object.keys(this.dataCache);
      const index = search(times, range);

      if (this.currentIndex === index) return null;

      if (index === -1) {
        this.currentIndex = 0;
        return null;
      }
      this.currentIndex = index;
      return this.dataCache[times[index]];
    },
    async onMapLoaded(map) {
      const { center, zoom } = this.config;
      this.$map = map;
      this.$map.setMapStyleV2({ styleJson: bmapTheme[this.theme] });
      map.centerAndZoom(new BMapGL.Point(center[0], center[1]), zoom);
      map.enableScrollWheelZoom();
      await this.initScene(this.config);
    },
    async initScene(config) {
      this.initMapEngine(config);
      this.initSky();
      await this.initModel();
      if (this.res) {
        this.progressBarConfig.status = 0;

        this.$nextTick(() => {
          this.$refs.progressBar.autoPlay(1);
        });
      } else {
        this.progressBarConfig.status = 1;
        this.$nextTick(() => {
          this.$refs.progressBar.autoPlay(0);
        });
      }
    },

    async initMapEngine(config) {
      const { center, pitch } = config;
      const engine = new Engine(this.$map, {
        rendering: {
          enableAnimationLoop: true
        }
      });
      engine.map.setCenter(center);
      engine.map.setPitch(pitch);
      this.$engine = engine;
    },

    initSky() {
      const sky = this.$engine.add(new EmptySky());
      sky.time = 3600 * 14;
      this.$sky = sky;
    },
    async initModel() {
      this.$model = new GeoModel({
        engine: this.$engine,
        max: this.max,
        type: 'line',
        prop: 'value',
        devicePath: '/static/models/uav.glb'
      });

      (await this.$model.init()).render();
    },

    initBoxDeviceHandler() {
      getWirelessBoxListServer(this.currentDeviceType).then(res => {
        this.boxData = res.data;
        this.sn = res.data[0].sn;
      });
    },
    initOriginDataHandler() {
      let obj = {
        sn: this.$route.query.sn,
        beginTime: this.beginTimeStr,
        endTime: this.endTimeStr,
        busiType: 2,
        dataIntervalType: 's'
        // offset: this.pageConfig.offset,
        // limit: 50
      };
      boxOriginDataServer(obj).then(res => {
        if (res.code !== 200) {
          this.$message.warning(res.message);
          this.$nextTick(() => {
            this.$refs.progressBar.autoPlay(0);
          });
          return;
        }
        if (isEmpty(res.data)) return this.$message('暂无回放数据');
        this.res = res.data;
        this.progressBarConfig.status = 0;

        this.$nextTick(() => {
          this.$refs.progressBar.autoPlay(1);
        });
        let snList = Object.keys(res.data);

        let sensorDict = this.sensorDict;
        snList.forEach(sn => {
          const { sensor, pageData } = res.data[sn];
          if (!sensor) return;
          // 字典处理
          if (this.currentDeviceType == ctt.MN_606) {
            sensor[0]['pm2.5浓度'] = 'pm25';
          }

          for (const key in sensor[0]) {
            sensorDict[res.data[sn].sensor[0][key]] = key;
          }

          pageData.forEach(async item => {
            let {
              time,
              latitude,
              longitude,
              weatherHumidity,
              weatherTemp,
              weatherWindDirection,
              weatherWindScale
            } = item;

            if (latitude === 0 && longitude === 0) return;

            const potions = toBmapPos2({
              coordinate: [longitude, latitude]
            });

            time = toTimestamp(time, 'ms');
            let position = [potions[0], potions[1], 0];

            const obj = {
              position,
              time,
              name: sn,
              weatherHumidity,
              weatherTemp,
              weatherWindDirection,
              weatherWindScale
            };

            Object.keys(sensorDict).forEach(key => {
              if (key == 'pm25' && this.currentDeviceType === ctt.MN_606) {
                item[key] = item[key] + 'ug/m³';
              }
              obj[sensorDict[key]] = item[key];
            });
            this.dataCache[time] = Object.assign(this.dataCache[time] || {}, obj);
          });
        });

        this.sensorList = Object.values(sensorDict);

        this.sensorList.forEach((item, index) => {
          this.sensorList[index] = {
            name: item,
            alarmState: 0,
            value: 0,
            unitName: ''
          };
        });
      });
    },
    async handleChange(time) {
      const data = this.matchData(time);
      this.loadNextPageHandle();

      if (isEmpty(data)) return;

      const temp = Object.values(this.dataCache).slice(0, this.currentIndex);

      this.sensorList.forEach(item => {
        if (data[item.name]) {
          item.value = data[item.name];
        }
      });

      this.weatherMod = {
        humidity: data.weatherHumidity, // 湿度
        temp: data.weatherTemp, //温度
        windDirection: data.weatherWindDirection, // 风向
        windSpeedValue: data.weatherWindScale // 风速
      };

      this.$model.setProp(this.sensorList[this.sensorindex].name);

      if (!this.firstChangePosition) {
        this.$map.centerAndZoom(
          new BMapGL.Point(temp[0].position[0], temp[0].position[1]),
          this.config.zoom
        );
        this.firstChangePosition = true;
      }
      this.$model.setData(this.sn, [data]);
      //   this.$model.setGradient({
      //     0: 'rgb(50, 250, 56)',
      //     0.5: 'rgb(250, 250, 56)',
      //     1: 'rgb(250, 50, 56)'
      //   });
    },

    handlePlayState(state) {
      this.$model.enableMove(state);
    },
    handleJumpto(time) {
      this.matchData(time);

      const temp = Object.values(this.dataCache).slice(
        0,
        this.currentIndex > 0 ? this.currentIndex : 0
      );

      if (this.currentIndex > 0) {
        this.$map.centerAndZoom(
          new BMapGL.Point(temp[temp.length - 1].position[0], temp[temp.length - 1].position[1]),
          this.config.zoom
        );

        this.$model.resetData(this.sn, temp);
      } else {
        this.$model.remove(this.sn);
      }
    },
    loadNextPageHandle() {
      let snList = Object.keys(this.res);
      snList.forEach(item => {
        let keys = Object.keys(this.res[item].sensor[0]);
        if (keys.includes(this.sensorList[0].name)) {
          this.pageConfig.allPages = this.res[item].pageData.pageSize;
          this.pageConfig.offset += 1;

          if (this.pageConfig.offset < this.pageConfig.allPages) {
            this.initOriginDataHandler();
          }
        }
      });
    },
    toggleSensorHandler(sensorName, sensorIndex) {
      this.sensorindex = sensorIndex;
      let snList = Object.keys(this.res);

      snList.forEach(item => {
        let keys = Object.keys(this.res[item].sensor[0]);
        if (keys.includes(sensorName)) {
          this.pageConfig.allPages = this.res[item].pageData.pageSize;
        }
      });
      this.progressBarConfig.status = 1;
      this.$nextTick(() => {
        this.$refs.progressBar.autoPlay(1);
      });

      this.$model.setProp(sensorName);
    },
    backHandle() {
      this.$router.back();
    },
    radioChangeHandle(e) {
      if (this.$model) {
        // const graphs = ['line', 'heatmap3D', 'heatmapBlock', 'point', 'pillar'];
        this.$model.showGraph(e);
      }
    }
  }
};
</script>

<style scoped lang="scss">
.trajectory-container {
  //   pointer-events: none;

  position: relative;
  width: 100%;
  height: 100%;
  .top-area {
    pointer-events: all;
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
    z-index: 6;
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;
    height: 100px;
    display: flex;
    justify-content: space-between;
    padding: 20px;
    box-sizing: border-box;
    .icon {
      pointer-events: all !important;
      // pointer-events: al
      cursor: pointer;
      font-size: 20px;
      font-weight: bold;
    }

    .replay-right {
      display: flex;
      align-items: center;
      .line-options {
        margin-right: 10px;
        .el-radio {
          display: block !important;
          margin-bottom: 10px;
        }
      }
    }
  }
  .map-control {
    height: calc(100% - 100px) !important;
  }
  .sensor-area {
    // height: 100px;
    // background-color: red;
    // left: 0;
    // top: 0;
    width: 100%;
    position: absolute;
    bottom: 100px;
    z-index: 6;
  }
}
</style>
